import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useUploadReasonFileMutation } from '@/app/services/periodApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateField } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

interface NewSendReasonProps {
  setDrawerSendAttachmentEight: (open: boolean) => void;
  selectedPeriod: string | null;
  monthYear: string;
}

export default function NewSendReason(props: NewSendReasonProps) {
  const { setDrawerSendAttachmentEight, selectedPeriod, monthYear } = props;
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const theme = useTheme();

  const { t } = useTranslation(['periods', 'common']);

  const [period, setPeriod] = useState<string | null>(selectedPeriod || null);

  const [uploadFile] = useUploadReasonFileMutation();

  const handleSubmit = async () => {
    setDrawerSendAttachmentEight(false);
    if (!selectedFile) {
      toast.error(t('common:processing.failed'));
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    await toast.promise(
      uploadFile({
        date: period || '',
        formData,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('historic.processing.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerSendAttachmentEight(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('import.reason')} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 4 }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('common:period')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {!selectedPeriod ? (
                <DateField
                  variant="outlined"
                  size="small"
                  value={period ? dayjs(period) : null}
                  format="MM-YYYY"
                  onChange={(newValue, context) => {
                    if (context.validationError !== null) {
                      return;
                    }

                    const date = newValue ? dayjs(newValue).startOf('M').format('YYYY-MM-DD') : null;

                    setPeriod(date);
                  }}
                />
              ) : (
                <Stack direction="row" spacing={2} sx={{ width: '20%' }}>
                  <TextField sx={{ flexGrow: 1 }} size="small" disabled value={monthYear || null} />
                </Stack>
              )}
            </LocalizationProvider>
          </Box>

          <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('upload.file')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField
                size="small"
                disabled
                value={selectedFile ? selectedFile.name : ''}
                placeholder={t('no.file.chosen') as string}
                sx={{
                  flexGrow: 1,
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.primary',
                  },
                }}
              />
              <Button
                disabled={!period}
                variant="contained"
                component="label"
                sx={{
                  minWidth: '40%',
                  backgroundColor: !period ? 'action.disabled' : 'primary.main',
                  color: !period ? 'text.disabled' : 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: !period ? 'action.disabled' : 'primary.dark',
                  },
                }}
              >
                <Typography sx={{ fontSize: 13 }}>{t('common:choose.file')}</Typography>
                <input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files) {
                      setSelectedFile(((e.target as HTMLInputElement).files || [null])[0]);
                    }
                  }}
                  hidden
                  multiple={false}
                />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!selectedFile} />
      </Box>
    </Box>
  );
}
